/* ==========================================================================
   SOLDIERS NUTRITION — Campanha Corrida
   JS único para as 3 páginas. Sem dependências.

   Responsabilidades:
   1. Captura e persistência de parâmetros de URL (UTMs + extras)
      URL -> sessionStorage -> localStorage (backup) -> links e campos ocultos
   2. Tracking: window.dataLayer + CustomEvent "soldiers:track"
      Eventos: PAGE_VIEW_CAMPAIGN, CLICK_CTA_CAMPAIGN, FORM_START,
               FORM_SUBMIT, FORM_SUCCESS, FORM_ERROR, PAGE_VIEW_THANK_YOU
   3. Formulário: validação, máscara de telefone, loading, anti-duplo envio,
      envio por webhook (fetch) OU envio nativo Shopify ({% form 'contact' %})
   4. Página de obrigado: só conta conversão após envio bem-sucedido

   Configuração vem por data-attributes no elemento raiz [data-soldiers-campaign]
   (preenchidos pelo Liquid a partir do schema da section).
   ========================================================================== */
(function () {
  'use strict';

  if (window.SoldiersCampaign && window.SoldiersCampaign.__loaded) return;

  var STORAGE_PREFIX = 'soldiers_campaign:';
  var KEY_PARAMS = STORAGE_PREFIX + 'params';
  var KEY_LEAD = STORAGE_PREFIX + 'lead';
  var KEY_LEAD_TRACKED = STORAGE_PREFIX + 'lead_tracked';
  var PARAMS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias no localStorage
  var LEAD_TTL_MS = 60 * 60 * 1000;             // 1 hora para acessar a página de obrigado
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  // Parâmetros internos do fluxo/Shopify que não devem ser persistidos como "custom"
  var IGNORED_PARAMS = ['contact_posted', 'customer_posted', 'form_type', 'sc_lead', 'view', 'preview_theme_id'];

  /* ------------------------------------------------------------------ */
  /* Storage seguro (Safari privado / storage bloqueado não pode quebrar) */
  /* ------------------------------------------------------------------ */
  function storage(kind) {
    var api = {
      get: function (key) {
        try { return window[kind].getItem(key); } catch (e) { return null; }
      },
      set: function (key, value) {
        try { window[kind].setItem(key, value); return true; } catch (e) { return false; }
      },
      remove: function (key) {
        try { window[kind].removeItem(key); } catch (e) { /* noop */ }
      }
    };
    return api;
  }
  var session = storage('sessionStorage');
  var local = storage('localStorage');

  function readJSON(store, key) {
    var raw = store.get(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function writeJSON(store, key, value) {
    return store.set(key, JSON.stringify(value));
  }

  /* ------------------------------------------------------------------ */
  /* Parâmetros de URL                                                    */
  /* ------------------------------------------------------------------ */
  function parseQuery(search) {
    var out = {};
    var q = (search || '').replace(/^\?/, '');
    if (!q) return out;
    q.split('&').forEach(function (pair) {
      if (!pair) return;
      var idx = pair.indexOf('=');
      var k = decodeURIComponent((idx >= 0 ? pair.slice(0, idx) : pair).replace(/\+/g, ' '));
      var v = idx >= 0 ? decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' ')) : '';
      if (!k || IGNORED_PARAMS.indexOf(k) !== -1) return;
      out[k] = v.slice(0, 500);
    });
    return out;
  }

  function toQuery(obj) {
    var parts = [];
    Object.keys(obj).forEach(function (k) {
      if (obj[k] === undefined || obj[k] === null || obj[k] === '') return;
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(obj[k]));
    });
    return parts.join('&');
  }

  var params = {
    /**
     * Lê a URL atual; se houver parâmetros, sobrescreve os salvos.
     * Se não houver, recupera do sessionStorage e, na falta, do localStorage.
     */
    capture: function () {
      var fromUrl = parseQuery(window.location.search);
      var stored = readJSON(session, KEY_PARAMS);
      if (!stored) {
        var backup = readJSON(local, KEY_PARAMS);
        if (backup && backup.__ts && (Date.now() - backup.__ts) < PARAMS_TTL_MS) {
          stored = backup;
        }
      }
      var merged = {};
      if (stored) {
        Object.keys(stored).forEach(function (k) {
          if (k.indexOf('__') !== 0) merged[k] = stored[k];
        });
      }
      Object.keys(fromUrl).forEach(function (k) { merged[k] = fromUrl[k]; });

      if (Object.keys(merged).length) {
        var record = {};
        Object.keys(merged).forEach(function (k) { record[k] = merged[k]; });
        record.__ts = Date.now();
        writeJSON(session, KEY_PARAMS, record);
        writeJSON(local, KEY_PARAMS, record);
      }
      return merged;
    },

    get: function () {
      var stored = readJSON(session, KEY_PARAMS) || readJSON(local, KEY_PARAMS) || {};
      var out = {};
      Object.keys(stored).forEach(function (k) {
        if (k.indexOf('__') !== 0) out[k] = stored[k];
      });
      return out;
    },

    utms: function () {
      var all = params.get();
      var out = {};
      UTM_KEYS.forEach(function (k) { out[k] = all[k] || ''; });
      return out;
    },

    extras: function () {
      var all = params.get();
      var out = {};
      Object.keys(all).forEach(function (k) {
        if (UTM_KEYS.indexOf(k) === -1) out[k] = all[k];
      });
      return out;
    },

    /** Acrescenta os parâmetros salvos a uma URL (mantendo os que ela já tiver). */
    appendTo: function (url) {
      var saved = params.get();
      if (!Object.keys(saved).length) return url;
      var hashIdx = url.indexOf('#');
      var hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
      var base = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
      var qIdx = base.indexOf('?');
      var existing = qIdx >= 0 ? parseQuery(base.slice(qIdx)) : {};
      var path = qIdx >= 0 ? base.slice(0, qIdx) : base;
      var final = {};
      Object.keys(saved).forEach(function (k) { final[k] = saved[k]; });
      Object.keys(existing).forEach(function (k) { final[k] = existing[k]; });
      var qs = toQuery(final);
      return path + (qs ? '?' + qs : '') + hash;
    },

    /** Aplica appendTo em todos os links marcados com [data-sc-keep-params]. */
    decorateLinks: function (root) {
      var links = (root || document).querySelectorAll('a[data-sc-keep-params]');
      Array.prototype.forEach.call(links, function (a) {
        var href = a.getAttribute('href');
        if (!href || /^(mailto:|tel:|javascript:)/i.test(href)) return;
        a.setAttribute('href', params.appendTo(href));
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Tracking                                                             */
  /* ------------------------------------------------------------------ */
  function track(eventName, data) {
    var payload = { event: eventName, campaign: config.campaign || 'corrida_soldiers' };
    var utms = params.utms();
    Object.keys(utms).forEach(function (k) { if (utms[k]) payload[k] = utms[k]; });
    if (data) Object.keys(data).forEach(function (k) { payload[k] = data[k]; });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    // Hooks opcionais: só disparam se as ferramentas já estiverem instaladas.
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, payload);
    } catch (e) { /* noop */ }
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', eventName, payload);
    } catch (e) { /* noop */ }

    try {
      document.dispatchEvent(new CustomEvent('soldiers:track', { detail: payload }));
    } catch (e) { /* navegadores antigos */ }

    if (config.debug) {
      try { console.info('[SoldiersCampaign]', eventName, payload); } catch (e) { /* noop */ }
    }
    return payload;
  }

  /* ------------------------------------------------------------------ */
  /* Lead flag (gate da página de obrigado)                               */
  /* ------------------------------------------------------------------ */
  var lead = {
    mark: function (source) {
      var record = { id: 'lead_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8), ts: Date.now(), source: source || 'webhook' };
      writeJSON(session, KEY_LEAD, record);
      writeJSON(local, KEY_LEAD, record);
      return record;
    },
    get: function () {
      var rec = readJSON(session, KEY_LEAD) || readJSON(local, KEY_LEAD);
      if (!rec || !rec.ts) return null;
      if ((Date.now() - rec.ts) > LEAD_TTL_MS) return null;
      return rec;
    },
    wasTracked: function (id) {
      return local.get(KEY_LEAD_TRACKED) === id || session.get(KEY_LEAD_TRACKED) === id;
    },
    setTracked: function (id) {
      session.set(KEY_LEAD_TRACKED, id);
      local.set(KEY_LEAD_TRACKED, id);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Utilidades de formulário                                             */
  /* ------------------------------------------------------------------ */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function onlyDigits(value) {
    return (value || '').replace(/\D+/g, '');
  }

  function maskPhoneBR(value) {
    var d = onlyDigits(value).slice(0, 11);
    if (d.length <= 2) return d.length ? '(' + d : '';
    if (d.length <= 6) return '(' + d.slice(0, 2) + ') ' + d.slice(2);
    if (d.length <= 10) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 6) + '-' + d.slice(6);
    return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7);
  }

  function validators(messages) {
    return {
      nome: function (value) {
        var v = (value || '').trim();
        if (!v) return messages.required;
        if (v.length < 3) return messages.nameShort;
        return '';
      },
      email: function (value) {
        var v = (value || '').trim();
        if (!v) return messages.required;
        if (!EMAIL_RE.test(v)) return messages.emailInvalid;
        return '';
      },
      telefone: function (value) {
        var d = onlyDigits(value);
        if (!d) return messages.required;
        if (d.length < 10 || d.length > 11) return messages.phoneInvalid;
        return '';
      }
    };
  }

  function setFieldError(field, message) {
    var input = field.querySelector('[data-sc-field]');
    var error = field.querySelector('.soldiers-campaign__error');
    if (!input) return;
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      if (error) {
        error.textContent = message;
        error.setAttribute('data-visible', 'true');
        input.setAttribute('aria-describedby', error.id);
      }
    } else {
      input.removeAttribute('aria-invalid');
      if (error) {
        error.textContent = '';
        error.removeAttribute('data-visible');
      }
    }
  }

  function setStatus(form, message, type) {
    var box = form.querySelector('.soldiers-campaign__form-status');
    if (!box) return;
    box.classList.remove('soldiers-campaign__form-status--success');
    if (!message) {
      box.textContent = '';
      box.removeAttribute('data-visible');
      return;
    }
    box.textContent = message;
    if (type === 'success') box.classList.add('soldiers-campaign__form-status--success');
    box.setAttribute('data-visible', 'true');
  }

  function setButtonState(button, state) {
    if (!button) return;
    button.setAttribute('data-state', state);
    if (state === 'loading') {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', 'disabled');
    } else if (state === 'done') {
      button.removeAttribute('aria-busy');
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
    }
  }

  function buildPayload(form) {
    var get = function (name) {
      var el = form.querySelector('[data-sc-field="' + name + '"]');
      return el ? el.value.trim() : '';
    };
    var utms = params.utms();
    var payload = {
      nome: get('nome'),
      email: get('email').toLowerCase(),
      telefone: get('telefone'),
      telefone_digits: onlyDigits(get('telefone')),
      campaign: config.campaign || 'corrida_soldiers',
      form_id: form.id || 'soldiers-campaign-form',
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      submitted_at: new Date().toISOString(),
      user_agent: navigator.userAgent
    };
    UTM_KEYS.forEach(function (k) { payload[k] = utms[k] || ''; });
    var extras = params.extras();
    if (Object.keys(extras).length) payload.extra_params = extras;
    // Campos adicionais opcionais (data-sc-field="algo") entram automaticamente
    var all = form.querySelectorAll('[data-sc-field]');
    Array.prototype.forEach.call(all, function (el) {
      var key = el.getAttribute('data-sc-field');
      if (!(key in payload)) payload[key] = el.value.trim();
    });
    return payload;
  }

  function flatten(obj, prefix, out) {
    out = out || {};
    Object.keys(obj).forEach(function (k) {
      var key = prefix ? prefix + '[' + k + ']' : k;
      var v = obj[k];
      if (v && typeof v === 'object') flatten(v, key, out);
      else out[key] = v == null ? '' : String(v);
    });
    return out;
  }

  function sendWebhook(url, format, payload) {
    var options = { method: 'POST', mode: 'cors', credentials: 'omit' };
    if (format === 'form') {
      options.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
      options.body = toQuery(flatten(payload));
    } else {
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify(payload);
    }
    if (typeof window.fetch !== 'function') {
      return Promise.reject(new Error('fetch indisponível'));
    }
    return window.fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res;
    });
  }

  function redirectToThankYou() {
    var url = config.thankYouUrl || '/pages/campanha-corrida-obrigado';
    window.location.assign(params.appendTo(url));
  }

  /* ------------------------------------------------------------------ */
  /* Inicialização por página                                             */
  /* ------------------------------------------------------------------ */
  var config = {};

  function readConfig(root) {
    var ds = root.dataset || {};
    config = {
      page: ds.scPage || '',
      campaign: ds.scCampaign || 'corrida_soldiers',
      debug: ds.scDebug === 'true',
      thankYouUrl: ds.scThankYouUrl || '',
      formUrl: ds.scFormUrl || '',
      submitMode: ds.scSubmitMode || 'native',   // 'webhook' | 'native'
      webhookUrl: ds.scWebhookUrl || '',
      webhookFormat: ds.scWebhookFormat || 'json', // 'json' | 'form'
      webhookFallbackNative: ds.scWebhookFallback !== 'false',
      // A section renderiza "pending" com o gate ligado e "granted" com ele desligado
      gateThankYou: ds.scGate === 'pending',
      posted: !!root.querySelector('[data-sc-posted="true"]'), // Shopify: form.posted_successfully?
      messages: {
        required: ds.scMsgRequired || 'Preencha este campo.',
        nameShort: ds.scMsgNameShort || 'Digite seu nome completo.',
        emailInvalid: ds.scMsgEmailInvalid || 'Digite um e-mail válido.',
        phoneInvalid: ds.scMsgPhoneInvalid || 'Digite um WhatsApp válido com DDD.',
        sendError: ds.scMsgSendError || 'Não foi possível enviar agora. Tente novamente.',
        sendSuccess: ds.scMsgSendSuccess || 'Cadastro enviado. Redirecionando...'
      }
    };
    return config;
  }

  function initCampaignPage(root) {
    track('PAGE_VIEW_CAMPAIGN', { page_type: 'campaign' });
    var ctas = root.querySelectorAll('[data-sc-cta]');
    Array.prototype.forEach.call(ctas, function (cta) {
      cta.addEventListener('click', function () {
        track('CLICK_CTA_CAMPAIGN', { cta_id: cta.getAttribute('data-sc-cta') || 'primary', cta_text: (cta.textContent || '').trim() });
      });
    });
  }

  function initFormPage(root) {
    var form = root.querySelector('form.soldiers-campaign__form');
    if (!form) return;
    form.setAttribute('novalidate', 'novalidate'); // validação própria, com mensagens amigáveis

    // Envio nativo Shopify concluído: marca lead e redireciona para obrigado.
    if (config.posted) {
      var rec = lead.mark('shopify_contact');
      track('FORM_SUCCESS', { submit_mode: 'native', lead_id: rec.id });
      setStatus(form, config.messages.sendSuccess, 'success');
      window.setTimeout(redirectToThankYou, 400);
      return;
    }

    var button = form.querySelector('[data-sc-submit]');
    var fields = form.querySelectorAll('.soldiers-campaign__field[data-sc-required]');
    var validate = validators(config.messages);
    var started = false;
    var submitting = false;

    // Campos ocultos (UTMs etc.) — também alimentam o e-mail do envio nativo
    function fillHidden() {
      var utms = params.utms();
      UTM_KEYS.forEach(function (k) {
        var el = form.querySelector('[data-sc-hidden="' + k + '"]');
        if (el) el.value = utms[k] || '';
      });
      var extras = form.querySelector('[data-sc-hidden="extra_params"]');
      if (extras) extras.value = JSON.stringify(params.extras());
      var pageUrl = form.querySelector('[data-sc-hidden="page_url"]');
      if (pageUrl) pageUrl.value = window.location.href;
      var referrer = form.querySelector('[data-sc-hidden="referrer"]');
      if (referrer) referrer.value = document.referrer || '';
      var body = form.querySelector('[data-sc-hidden="body"]');
      if (body) {
        var p = buildPayload(form);
        var lines = ['Campanha: ' + p.campaign, 'Nome: ' + p.nome, 'E-mail: ' + p.email, 'WhatsApp: ' + p.telefone];
        UTM_KEYS.forEach(function (k) { if (p[k]) lines.push(k + ': ' + p[k]); });
        if (p.extra_params) lines.push('Outros parâmetros: ' + JSON.stringify(p.extra_params));
        lines.push('Página: ' + p.page_url);
        body.value = lines.join('\n');
      }
    }
    fillHidden();

    // Máscara de telefone
    var phone = form.querySelector('[data-sc-field="telefone"]');
    if (phone) {
      phone.addEventListener('input', function () {
        phone.value = maskPhoneBR(phone.value);
      });
    }

    // Voltar pelo histórico (bfcache) não pode deixar o botão travado
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        submitting = false;
        setButtonState(button, 'idle');
      }
    });

    // FORM_START (uma vez) + validação ao sair do campo
    Array.prototype.forEach.call(fields, function (field) {
      var input = field.querySelector('[data-sc-field]');
      if (!input) return;
      var name = input.getAttribute('data-sc-field');
      input.addEventListener('focus', function () {
        if (started) return;
        started = true;
        track('FORM_START', { first_field: name });
      });
      input.addEventListener('blur', function () {
        if (!input.value) return;
        var fn = validate[name];
        setFieldError(field, fn ? fn(input.value) : '');
      });
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') {
          var fn = validate[name];
          if (fn && !fn(input.value)) setFieldError(field, '');
        }
      });
    });

    function validateAll() {
      var firstInvalid = null;
      Array.prototype.forEach.call(fields, function (field) {
        var input = field.querySelector('[data-sc-field]');
        if (!input) return;
        var name = input.getAttribute('data-sc-field');
        var fn = validate[name];
        var msg = fn ? fn(input.value) : (input.value.trim() ? '' : config.messages.required);
        setFieldError(field, msg);
        if (msg && !firstInvalid) firstInvalid = input;
      });
      return firstInvalid;
    }

    form.addEventListener('submit', function (event) {
      if (submitting) { event.preventDefault(); return; }

      // Honeypot: bots preenchem; humanos não veem
      var trap = form.querySelector('[data-sc-honeypot]');
      if (trap && trap.value) { event.preventDefault(); return; }

      var firstInvalid = validateAll();
      if (firstInvalid) {
        event.preventDefault();
        firstInvalid.focus();
        return;
      }

      fillHidden();
      submitting = true;
      setStatus(form, '');
      setButtonState(button, 'loading');

      var useWebhook = config.submitMode === 'webhook' && config.webhookUrl;
      track('FORM_SUBMIT', { submit_mode: useWebhook ? 'webhook' : 'native', fallback: !!config.fallback });

      if (!useWebhook) {
        // Envio nativo: o navegador segue com o POST para o Shopify.
        return;
      }

      event.preventDefault();
      var payload = buildPayload(form);

      sendWebhook(config.webhookUrl, config.webhookFormat, payload)
        .then(function () {
          var rec = lead.mark('webhook');
          track('FORM_SUCCESS', { submit_mode: 'webhook', lead_id: rec.id });
          setButtonState(button, 'done');
          setStatus(form, config.messages.sendSuccess, 'success');
          window.setTimeout(redirectToThankYou, 600);
        })
        .catch(function (err) {
          track('FORM_ERROR', { submit_mode: 'webhook', error: String(err && err.message || err) });
          if (config.webhookFallbackNative && form.getAttribute('action')) {
            // Rede de segurança: reenvia pelo formulário nativo do Shopify.
            // Libera o bloqueio e dispara o submit de novo; o handler segue o modo nativo.
            submitting = false;
            config.submitMode = 'native';
            config.fallback = true;
            if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit();
            return;
          }
          submitting = false;
          setButtonState(button, 'idle');
          setStatus(form, config.messages.sendError, 'error');
        });
    });
  }

  function initThankYouPage(root) {
    var rec = lead.get();
    if (!rec && config.gateThankYou) {
      root.setAttribute('data-sc-gate', 'denied');
      // Acesso direto sem cadastro: leva de volta ao formulário sem contar conversão.
      var target = config.formUrl || '/pages/campanha-corrida-formulario';
      window.setTimeout(function () {
        window.location.replace(params.appendTo(target));
      }, 1200);
      return;
    }
    root.setAttribute('data-sc-gate', 'granted');
    if (rec && !lead.wasTracked(rec.id)) {
      lead.setTracked(rec.id);
      track('PAGE_VIEW_THANK_YOU', { page_type: 'thank_you', lead_id: rec.id, lead_source: rec.source });
    } else if (!rec) {
      track('PAGE_VIEW_THANK_YOU', { page_type: 'thank_you', gated: false });
    }
  }

  function init() {
    var root = document.querySelector('[data-soldiers-campaign]');
    if (!root) return;
    readConfig(root);
    params.capture();
    params.decorateLinks(document);

    if (config.page === 'campaign') initCampaignPage(root);
    else if (config.page === 'form') initFormPage(root);
    else if (config.page === 'thank-you') initThankYouPage(root);
  }

  window.SoldiersCampaign = {
    __loaded: true,
    params: params,
    track: track,
    lead: lead,
    config: function () { return config; },
    maskPhoneBR: maskPhoneBR
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
